// Offer + intake step model. The whole intake flow is config-driven: an
// OfferConfig is just a typed list of steps the IntakeFlow renders one at a time.

export type IntakeStepType =
  | "statement"
  | "text"
  | "textarea"
  | "singleChoice"
  | "multiChoice"
  | "email"
  | "number"
  | "fileUpload"
  | "review";

/** Shared validation knobs available to every input-bearing step. */
export interface StepValidation {
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  /** Source string for a RegExp the answer must match. */
  pattern?: string;
  /** Custom message shown when validation fails. */
  message?: string;
}

interface BaseStep {
  id: string;
  type: IntakeStepType;
  title: string;
  description?: string;
  required?: boolean;
  helpText?: string;
  placeholder?: string;
  validation?: StepValidation;
}

export interface StatementStep extends BaseStep {
  type: "statement";
  /** Label for the continue button (defaults to "Continue"). */
  ctaLabel?: string;
}

export interface TextStep extends BaseStep {
  type: "text";
}

export interface TextAreaStep extends BaseStep {
  type: "textarea";
  rows?: number;
}

export interface ChoiceOption {
  /** Stored value. */
  value: string;
  /** Display label (defaults to value). */
  label?: string;
  description?: string;
}

export interface SingleChoiceStep extends BaseStep {
  type: "singleChoice";
  options: Array<string | ChoiceOption>;
  /** Auto-advance to the next step on selection (default true). */
  autoAdvance?: boolean;
}

export interface MultiChoiceStep extends BaseStep {
  type: "multiChoice";
  options: Array<string | ChoiceOption>;
  minSelections?: number;
  maxSelections?: number;
}

export interface EmailStep extends BaseStep {
  type: "email";
}

export interface NumberStep extends BaseStep {
  type: "number";
}

export interface FileUploadStep extends BaseStep {
  type: "fileUpload";
  maxFiles?: number;
  minFiles?: number;
  /** Allowed extensions, e.g. [".pdf", ".docx"]. */
  allowedFileTypes?: string[];
  /** Per-file size cap in megabytes. */
  maxFileSizeMb?: number;
  /**
   * Optional ordered-upload callout shown prominently above the dropzone.
   * Used when upload order is significant (e.g. first file = regulation, rest = SOPs).
   */
  orderedUploadHint?: {
    title: string;
    items: string[];
  };
}

export interface ReviewStep extends BaseStep {
  type: "review";
  /** Label for the submit button (defaults to "Submit"). */
  submitLabel?: string;
  /** Optional consent/legal text shown above the submit button. */
  consentText?: string;
}

export type IntakeStep =
  | StatementStep
  | TextStep
  | TextAreaStep
  | SingleChoiceStep
  | MultiChoiceStep
  | EmailStep
  | NumberStep
  | FileUploadStep
  | ReviewStep;

export type OfferPipeline = "obligations-export" | "sop-coverage";

export interface OfferPricing {
  /** Marketing copy shown before the quote is computed, e.g. "From $29". */
  fromPriceLabel?: string;
  /** Typical price range copy, e.g. "Typically €297–€747 depending on document size". */
  priceRangeLabel?: string;
  currency?: string;
}

export interface OfferConfig {
  offerId: string;
  pipeline: OfferPipeline;
  title: string;
  subtitle?: string;
  /** Short trust/marketing copy shown on the start screen. */
  intro?: string;
  /** Estimated minutes to complete, surfaced on the start screen. */
  estimatedMinutes?: number;
  /** Optional free-form duration copy; takes precedence over estimatedMinutes. */
  estimatedDurationLabel?: string;
  pricing?: OfferPricing;
  steps: IntakeStep[];
}

/** Normalize a `string | ChoiceOption` option to a ChoiceOption. */
export function toChoiceOption(option: string | ChoiceOption): ChoiceOption {
  return typeof option === "string" ? { value: option, label: option } : option;
}
